import type { CSSProperties, FunctionComponent, JSX } from "preact";
import NiceModal, { type NiceModalHocProps, useModal } from "@ebay/nice-modal-react";
import * as stylex from "@stylexjs/stylex";
import { useEffect, useImperativeHandle, useRef, useState } from "preact/hooks";

import closeButtonSprite from "../assets/close.svg";
import { forwardRef } from "preact/compat";
import Button from "./Button.tsx";
import Spinner from "./Spinner.tsx";

const styles = stylex.create({
    container: {
        position: "absolute",
        top: 0,
        width: "100%",
        height: "100%",
        zIndex: 700,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "hwb(0 100 0 / .5)",
    },
    modal: {
        position: "relative",
        padding: "20px",
        borderWidth: "10px",
        backgroundColor: "#FFF",
        borderRadius: 5,
        borderColor: "hwb(0 0 100 / .4)",
        borderStyle: "solid",
        width: "80%",
        minHeight: "80%",
    },
    modalContent: {
        overflowY: "auto",
    },
    paddingLessModal: {
        padding: 0,
    },
    closeButton: {
        position: "absolute",
        top: 5,
        right: 5,
        width: 12,
        height: 12,
        zIndex: 100,
        cursor: "pointer",
    },
    wizardContainer: {
        height: 460,
        display: "grid",
        gridTemplateRows: "1fr 10px",
    },
    wizardSteps: {
        width: "100%",
        position: "relative",
        overflowX: "hidden",
    },
    wizardStepsInternal: {
        position: "absolute",
        display: "flex",
        height: "100%",
        left: 0,
        top: 0,
        transition: ".2s left",
    },
    wizardStep: {
        display: "inline-block",
        padding: "20px",
        width: 460,
    },
    wizardProgress: {
        backgroundColor: "#31AAFB",
        height: "100%",
        width: 0,
        transition: ".2s width, .1s opacity",
    },
    alertHeader: {
        fontFamily: "'Inter Variable', sans-serif",
        textAlign: "center",
        fontSize: 16,
        color: "#666",
        marginBottom: 20,
    },
    alertButtons: {
        gap: 10,
        display: "flex",
        justifyContent: "center",
    },
});

const Abstract: FunctionComponent<{
    onClose?: () => boolean;
    closable?: boolean;
    padding?: boolean;
    style?: CSSProperties;
    mcStyle?: CSSProperties;
}> = ({
    padding = true,
    closable = true,
    style = {},
    mcStyle = { maxHeight: "85vh" },
    onClose = () => true,
    children,
}) => {
    const modal = useModal();
    const container = useRef<HTMLDivElement>(null);
    useEffect(() => {
        let con = container.current!;
        let root = con.parentElement as HTMLDivElement;
        let oldScrollTop = root.scrollTop;

        root.scrollTop = 0;
        root.style.overflow = "hidden";

        return () => {
            root.style.overflow = "";
            root.scrollTo(0, oldScrollTop);
        };
    }, []);

    return (
        <div ref={container} {...stylex.props(styles.container)}>
            <dialog
                open
                {...stylex.props(styles.modal, !padding && styles.paddingLessModal)}
                style={style}
            >
                {closable && (
                    <img
                        {...stylex.props(styles.closeButton)}
                        alt="Close dialog"
                        src={closeButtonSprite}
                        onClick={
                            modal &&
                            (() => {
                                if (onClose()) {
                                    modal.resolve(null);
                                    modal.remove();
                                }
                            })
                        }
                    />
                )}

                <div {...stylex.props(styles.modalContent)} style={mcStyle}>
                    {children}
                </div>
            </dialog>
        </div>
    );
};

interface WizardContext {
    resolve: (result: any) => void;
    advance: () => void;
    revert: () => void;
    setStep: (step: number) => void;
    index: number;
    hasNext: boolean;
    hasPrev: boolean;
}

export type WizardStep = (ctx: WizardContext) => JSX.Element;

interface StepGeometry {
    width: number;
    height: number;
    containerMaxHeight: string;
}

interface WizardProps {
    closable?: boolean;
    steps: Array<WizardStep>;
    onStepChanged?: ((step: number) => void) | ((step: number) => Promise<void>);
    progressBarVisible?: boolean;
    stepGeometry?: StepGeometry;
    style?: CSSProperties;
}

export interface WizardElement {
    currentStep: number;
    setStep(step: number): void;
    advance(): void;
    revert(): void;
}

const Wizard = forwardRef<WizardElement, WizardProps>(
    (
        {
            closable,
            steps,
            onStepChanged = () => {},
            progressBarVisible = true,
            stepGeometry = {
                width: 460,
                height: 450,
                containerMaxHeight: "85vh",
            },
            style = {},
        },
        ref,
    ) => {
        const modal = useModal();
        const [currentIndex, setCurrentIndex] = useState(0);
        let maxIndex = steps.length - 1;

        useEffect(() => {
            setCurrentIndex((i) => Math.min(maxIndex, i));
        }, [JSON.stringify(steps)]);

        const advance = () => setCurrentIndex((i) => Math.min(i + 1, maxIndex));
        const revert = () => setCurrentIndex((i) => Math.max(i - 1, 0));
        const setStep = (step: number) => setCurrentIndex(Math.min(maxIndex, step));
        useImperativeHandle(ref, () => ({
            currentStep: currentIndex,
            advance: advance,
            revert: revert,
            setStep: setStep,
        }));

        useEffect(() => {
            void onStepChanged(currentIndex);
        }, [currentIndex]);

        return (
            <Abstract
                closable={closable}
                padding={false}
                style={style}
                mcStyle={{ maxHeight: stepGeometry.containerMaxHeight }}
            >
                <div
                    {...stylex.props(styles.wizardContainer)}
                    style={{ height: stepGeometry.height + 10 }}
                >
                    <div {...stylex.props(styles.wizardSteps)}>
                        <div
                            {...stylex.props(styles.wizardStepsInternal)}
                            style={{ left: `${-1 * stepGeometry.width * currentIndex}px` }}
                        >
                            {steps.map((Step, idx) => (
                                <div
                                    {...stylex.props(styles.wizardStep)}
                                    key={idx}
                                    style={{
                                        height: stepGeometry.height,
                                        width: stepGeometry.width,
                                    }}
                                >
                                    <Step
                                        resolve={(result: any) => {
                                            modal.resolve(result);
                                            modal.remove();
                                        }}
                                        setStep={setStep}
                                        advance={advance}
                                        revert={revert}
                                        index={idx}
                                        hasNext={idx != maxIndex}
                                        hasPrev={idx > 0}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div>
                        <div
                            {...stylex.props(styles.wizardProgress)}
                            style={{
                                width: `${maxIndex ? (100 * currentIndex) / maxIndex : 0}%`,
                                opacity: progressBarVisible ? 1 : 0,
                            }}
                        ></div>
                    </div>
                </div>
            </Abstract>
        );
    },
);

const Alert: FunctionComponent<
    NiceModalHocProps & {
        title: string;
        okLabel?: string;
    }
> = NiceModal.create(({ title, okLabel = "Хорошо" }) => {
    const modal = useModal();

    return (
        <Abstract style={{ minHeight: "unset" }} closable>
            <div>
                <div {...stylex.props(styles.alertHeader)}>{title}</div>
                <div {...stylex.props(styles.alertButtons)}>
                    <Button
                        label={okLabel}
                        kind="success"
                        onClick={() => {
                            modal.resolve();
                            modal.remove();
                        }}
                    />
                </div>
            </div>
        </Abstract>
    );
});

const Loader: FunctionComponent<NiceModalHocProps & { title: string }> = NiceModal.create(
    ({ title }) => {
        return (
            <Abstract>
                <Spinner.Loader label={title} style={{ height: 420 }} />
            </Abstract>
        );
    },
);

const useLoader = async (title: string, cb: () => Promise<void>): Promise<any | null> => {
    NiceModal.show(Loader, { title });
    try {
        await cb();
    } catch (error) {
        NiceModal.remove(Loader);

        let message: string;
        if (typeof error == "object" && error instanceof Error) message = error.message;
        else if (typeof error == "string") message = error;
        else message = JSON.stringify(error);

        await NiceModal.show(Alert, { title: message });

        return error;
    }

    NiceModal.remove(Loader);

    return null;
};

export default {
    useLoader,
    Abstract,
    Loader,
    Wizard,
    Alert,
};
